// Tech phone app data layer: cached reads + an offline write queue (IndexedDB) that syncs when signal returns.
import { getToken } from "../software/api";

const B = process.env.REACT_APP_BACKEND_URL;
let dbp;
const idb = () => dbp || (dbp = new Promise((res, rej) => {
  const r = indexedDB.open("aqx-tech", 1);
  r.onupgradeneeded = () => r.result.createObjectStore("q", { keyPath: "key" });
  r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error);
}));
const tx = async (mode, fn) => { const d = await idb(); return new Promise((res, rej) => { const t = d.transaction("q", mode); const out = fn(t.objectStore("q")); t.oncomplete = () => res(out?.result ?? out); t.onerror = () => rej(t.error); }); };

const hdr = () => ({ Authorization: `Bearer ${getToken()}` });
export const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// cached copies belong to one person: switching login on the same phone wipes them
export const claimCache = (userId) => {
  if (localStorage.getItem("tc:owner") === userId) return;
  Object.keys(localStorage).filter((k) => k.startsWith("tc:")).forEach((k) => localStorage.removeItem(k));
  localStorage.setItem("tc:owner", userId || "");
};

export async function get(path) {
  try {
    const r = await fetch(`${B}/api${path}`, { headers: hdr() });
    if (!r.ok) throw Object.assign(new Error((await r.json().catch(() => ({}))).detail || "Error"), { status: r.status });
    const d = await r.json();
    try { localStorage.setItem(`tc:${path}`, JSON.stringify(d)); } catch { /* too big to cache offline */ }
    return d;
  } catch (e) {
    const c = localStorage.getItem(`tc:${path}`);
    if (c && !e.status) return JSON.parse(c);
    throw e;
  }
}

// live call (needs signal) — for AI photo reads and searches
export const fileUrl = (u) => (u && u.startsWith("/") ? `${B}${u}` : u);
export async function live(path) {
  const r = await fetch(`${B}/api${path}`, { headers: hdr() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || "Something went wrong");
  return d;
}
export async function post(path, body) {
  const r = await fetch(`${B}/api${path}`, { method: "POST", headers: { ...hdr(), "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || "Something went wrong");
  return d;
}

// queued write: same key replaces an older unsent one (e.g. the latest visit state wins)
export async function queue(key, method, path, body, file) {
  await tx("readwrite", (s) => s.put({ key, method, path, body, file, ts: Date.now() }));
  window.dispatchEvent(new Event("tech:queue"));
  flush();
}

export const pending = () => tx("readonly", (s) => s.count());

let flushing = false, again = false;
export async function flush() {
  if (!navigator.onLine) return;
  if (flushing) { again = true; return; }
  flushing = true; again = false;
  try {
    const items = (await tx("readonly", (s) => s.getAll())).sort((a, b) => a.ts - b.ts);
    for (const it of items) {
      let r;
      try {
        if (it.file) {
          const fd = new FormData();
          Object.entries(it.body || {}).forEach(([k, v]) => fd.append(k, v));
          fd.append("file", it.file, it.body?.name || "photo.jpg");
          r = await fetch(`${B}/api${it.path}`, { method: it.method, headers: hdr(), body: fd });
        } else {
          r = await fetch(`${B}/api${it.path}`, { method: it.method, headers: { ...hdr(), "Content-Type": "application/json" }, body: JSON.stringify(it.body) });
        }
      } catch { break; }
      if (r.status === 401) break;
      if (!r.ok && r.status >= 500 && it.path === "/tech/test-strip-photo" && (it.tries || 0) < 5) {  // AI hiccup: retry later without holding up the rest
        const cur = await tx("readonly", (s) => s.get(it.key));
        if (cur && cur.ts === it.ts) await tx("readwrite", (s) => s.put({ ...it, tries: (it.tries || 0) + 1 }));
        continue;
      }
      if (!r.ok && r.status >= 500 && !["/tech/test-strip-photo", "/tech/sms"].includes(it.path)) break;
      if (r.status === 404 && ((await r.clone().json().catch(() => ({}))).detail || "Not Found") === "Not Found") break; // server not updated yet — keep and retry
      if (!r.ok) { const det = (await r.json().catch(() => ({}))).detail || `Couldn't save (${r.status})`; localStorage.setItem("tech:last-error", it.path === "/tech/sms" ? `Text not sent: ${det}` : det); }
      const cur = await tx("readonly", (s) => s.get(it.key));
      if (cur && cur.ts === it.ts) await tx("readwrite", (s) => s.delete(it.key));
    }
  } finally {
    flushing = false;
    window.dispatchEvent(new Event("tech:queue"));
    if (again) setTimeout(flush, 0);
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", flush);
  setInterval(flush, 20000);
}

// re-send every visit kept on this phone from the last week (safe: the server upserts and locks finished visits)
export function resyncVisits() {
  const week = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
  Object.keys(localStorage).filter((k) => k.startsWith("tv:")).forEach((k) => {
    const v = JSON.parse(localStorage.getItem(k) || "null");
    if (v?.client_id && (v.date || "") >= week) queue(`visit:${v.id}`, "POST", "/tech/visit", v);
  });
}

// local visit state per stop
export const visitKey = (job, clientId, date) => job?.visit_id || `v-${job?.id || clientId}-${date}`;
export const loadVisit = (id) => JSON.parse(localStorage.getItem(`tv:${id}`) || "null");
export function saveVisit(v) {
  localStorage.setItem(`tv:${v.id}`, JSON.stringify(v));
  queue(`visit:${v.id}`, "POST", "/tech/visit", v);
}

export const todayISO = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };
export const nowHM = () => new Date().toTimeString().slice(0, 5);
export const fileToB64 = (f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });

// shrink camera photos before upload/AI (phones shoot 4-12MB)
export async function shrink(file, max = 1800) {
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = URL.createObjectURL(file); });
  const k = Math.min(1, max / Math.max(img.width, img.height));
  const c = document.createElement("canvas"); c.width = img.width * k; c.height = img.height * k;
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return new Promise((res) => c.toBlob((b) => res(b || file), "image/jpeg", 0.85));
}

// Client texts go from the TECH'S OWN PHONE: opens the phone's Messages app ready to send (works offline too)
export function phoneText(clientIds, msg, to, raw) {
  const cls = [].concat(clientIds).map((id) => JSON.parse(localStorage.getItem(`tc:/tech/client/${id}`) || "{}"));
  const nums = (to ? [to] : cls.map((c) => c.mobile)).map((n) => String(n || "").replace(/[^\d+]/g, "")).filter(Boolean);
  if (!nums.length) return false;
  const me = (JSON.parse(localStorage.getItem("tech:user") || "{}").name || "").split(" ")[0];
  const first = cls.length === 1 ? (cls[0].first_name || (cls[0].name || "").split(" ")[0] || "there").trim() : "there";
  const body = raw ? msg : `Hi ${first}, it's ${me} from Glamour Pools and Spas. ${msg}`;
  window.location.href = `sms:${nums.join(",")}${/iPhone|iPad|iPod/.test(navigator.userAgent) ? "&" : "?"}body=${encodeURIComponent(body)}`;
  return true;
}
